import { FaPlus, FaRedo } from 'react-icons/fa';
import useGet from '../Hooks/useGet';
import { API_ENDPOINTS } from '../Config/api';
import ModalWrapper from '../Components/UI/ModalWrapper';
import { useState, useEffect } from 'react';
import useDelete from '../Hooks/useDelete';
import { toast } from 'react-toastify';
import ConfirmDelete from '../Components/Modal/ConfirmDelete';
import CreateEditAsset from '../Components/Modal/CreateEditAsset';
import Alert from '../Components/Modal/Alert';
import usePost from '../Hooks/usePost';
import usePatch from '../Hooks/usePatch';
import capitalizeFirstLetter from '../Utils/capitalizeFirstLetter'
import formatDate from '../Utils/formatDate';
import DynamicTable from '../Components/UI/DynamicTable'
import { Icon } from '../Components/UI/Icon';
import { useTranslation } from 'react-i18next';


export default function AssetTable() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [childrenModal, setChildrenModal] = useState(null);

    const { fetchedData: initialAssets } = useGet(API_ENDPOINTS.allCompanyAssets);
    const { postData } = usePost();
    const { patchData } = usePatch();
    const { deleteData } = useDelete();
    const { t } = useTranslation();
    const [assets, setAssets] = useState([]);
    const [resendingId, setResendingId] = useState(null);

    useEffect(() => {
        if (initialAssets) setAssets(initialAssets);
    }, [initialAssets]);

    const handleCloseModal = () => {
        setChildrenModal(null);
        setIsModalOpen(false);
    };

    const handleOpenModal = (content) => {
        setChildrenModal(content);
        setIsModalOpen(true);
    };

    const handleDelete = async (idAsset) => {
        try {
            await deleteData(API_ENDPOINTS.deleteAsset(idAsset));
            setAssets(prev => prev.filter(a => a.id_asset !== idAsset));
            toast.success(t('myAssets.deleteSuccess') || 'Asset deleted successfully!');
            handleCloseModal();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || t('myAssets.deleteFailed') || "Unable to delete asset.";
            toast.error(errorMessage);
            console.error("Error deleting asset:", err);
        }
    };

    const handleCreate = async (newAsset) => {
        // Controlla duplicati
        const duplicate = assets.find(
            a => a.asset.trim().toLowerCase() === newAsset.asset.trim().toLowerCase() &&
                 a.tipo_asset === newAsset.tipo_asset
        );
        if (duplicate) {
            toast.error(t('myAssets.duplicateAsset') || 'Questo asset esiste già!');
            handleCloseModal();
            return;
        }
        try {
            const response = await postData(API_ENDPOINTS.createNewAsset, newAsset);
            setAssets(prev => [...prev, response]);
            if (response.stato_verifica === 'pending') {
                if (response.tipo_asset === 'ip') {
                    toast.info(
                        t('myAssets.verification.dnsTxtInfo', {
                            record: `_aidomo-verify.${response.associated_domain}`,
                            value: response.dns_txt_value
                        }) || `Aggiungi un record DNS TXT: _aidomo-verify.${response.associated_domain} → ${response.dns_txt_value}`,
                        { autoClose: false }
                    );
                } else {
                    toast.info(t('myAssets.verification.emailSent') || 'Email di verifica inviata. Controlla la casella di posta per autorizzare il monitoraggio.', { autoClose: 8000 });
                }
            }
            toast.success(t('myAssets.createSuccess') || 'Asset created successfully!');
            handleCloseModal();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || t('myAssets.createFailed') || "Unable to create asset.";
            toast.error(errorMessage);
            console.error("Error creating asset:", err);
        }
    };

    const handleEdit = async (idAsset, updatedValues) => {
        // Controlla duplicati (escludi l'asset corrente)
        const duplicate = assets.find(
            a => a.id_asset !== idAsset &&
                 a.asset.trim().toLowerCase() === updatedValues.asset.trim().toLowerCase() &&
                 a.tipo_asset === updatedValues.tipo_asset
        );
        if (duplicate) {
            toast.error(t('myAssets.duplicateAsset') || 'Questo asset esiste già!');
            handleCloseModal();
            return;
        }
        try {
            const response = await patchData(API_ENDPOINTS.updateAsset(idAsset), updatedValues);
            setAssets(prev => prev.map(a => a.id_asset === idAsset ? response : a));
            toast.success(t('myAssets.updateSuccess') || 'Asset updated successfully!');
            handleCloseModal();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || t('myAssets.updateFailed') || "Unable to update asset.";
            toast.error(errorMessage);
            console.error("Error updating asset:", err);
        }
    };

    return (
        <div className="p-2 sm:p-5" data-tour="myassets-content">
            {/* ADD ASSET */}
            <div className='flex w-full max-w-7xl justify-end'>
                <button
                    className="mb-5 flex items-center gap-2 bg-white text-gray-800 shadow-md hover:shadow-lg transition-all duration-300 px-3 py-2 rounded-md active:scale-95 "
                    onClick={() => handleOpenModal(
                        assets.length >= 10 ? (
                            <Alert
                                type='warning'
                                onClose={handleCloseModal}
                                title={t('myAssets.addLimitTitle')}
                                message={t('myAssets.addLimitMessage')}
                            />
                        ) : (
                            <CreateEditAsset
                                onclose={handleCloseModal}
                                onSubmit={handleCreate}
                            />
                        )
                    )}
                >
                    <FaPlus size={18} />
                    {t('myAssets.addAsset')}
                </button>
            </div>

            <DynamicTable
                data={assets}
                customColumnsMap={[
                    {
                        field: 'asset',
                        label: t('myAssets.column.asset'),
                        width: 'w-48'
                    },
                    {
                        field: 'tipo_asset',
                        label: t('myAssets.column.type'),
                        width: 'w-48',
                        align: 'left',
                        render: (row) => (
                            <div>{capitalizeFirstLetter(row.tipo_asset || '')}</div>
                        )
                    },
                    {
                        field: 'stato_verifica',
                        label: t('myAssets.column.verification'),
                        width: 'w-48',
                        align: 'left',
                        render: (row) => {
                            const isPending = row.stato_verifica === 'pending';
                            return (
                                <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        isPending
                                            ? 'bg-amber-100 text-amber-800'
                                            : 'bg-green-100 text-green-800'
                                    }`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${
                                            isPending ? 'bg-amber-500' : 'bg-green-500'
                                        }`} />
                                        {isPending
                                            ? t('myAssets.verification.pending')
                                            : t('myAssets.verification.verified')
                                        }
                                    </span>
                                </div>
                            );
                        }
                    },
                    {
                        field: 'flag_attivo',
                        label: t('myAssets.column.status'),
                        width: 'w-48',
                        align: 'left',
                        render: (row) => (
                            <div>{row.flag_attivo ? t('myAssets.status.active') : t('myAssets.status.inactive')}</div>
                        )
                    },
                    {
                        field: 'data_registrazione',
                        label: t('myAssets.column.registrationDate'),
                        width: 'w-48',
                        align: 'left',
                        render: (row) => (
                            <div>{formatDate(row.data_registrazione)}</div>
                        )
                    },
                    {
                        field: 'data_scadenza',
                        label: t('myAssets.column.expirationDate'),
                        width: 'w-48',
                        align: 'left',
                        render: (row) => (
                            <div>{row.data_scadenza ? formatDate(row.data_scadenza) : '-'}</div>
                        )
                    },
                    {
                        label: '',
                        width: 'w-32',
                        align: 'right',
                        render: (row) => (
                            <div className="flex items-center justify-end gap-2">
                                {row.stato_verifica === 'pending' && (
                                    <button
                                        className='text-sm active:scale-95 text-amber-600 hover:text-amber-700 duration-300 hover:scale-110 disabled:opacity-50'
                                        disabled={resendingId === row.id_asset}
                                        title={row.tipo_asset === 'ip'
                                            ? t('myAssets.verification.checkDns')
                                            : t('myAssets.verification.resend')
                                        }
                                        onClick={async () => {
                                            setResendingId(row.id_asset);
                                            try {
                                                if (row.tipo_asset === 'ip') {
                                                    await postData(API_ENDPOINTS.assetCheckDns(row.id_asset));
                                                    toast.info(t('myAssets.verification.dnsCheckStarted') || 'Verifica DNS avviata.');
                                                } else {
                                                    await postData(API_ENDPOINTS.assetResendVerification(row.id_asset));
                                                    toast.success(t('myAssets.verification.resendSuccess') || 'Email reinviata.');
                                                }
                                            } catch (err) {
                                                toast.error(err.response?.data?.detail || t('myAssets.verification.resendFailed') || 'Errore.');
                                            } finally {
                                                setResendingId(null);
                                            }
                                        }}
                                    >
                                        <FaRedo size={14} className={resendingId === row.id_asset ? 'animate-spin' : ''} />
                                    </button>
                                )}
                                <button
                                    className='text-lg active:scale-95 text-gray-500 hover:text-gray-600 duration-300 hover:scale-110'
                                    onClick={() => handleOpenModal(
                                        <CreateEditAsset
                                            onclose={handleCloseModal}
                                            onSubmit={values => handleEdit(row.id_asset, values)}
                                            defaultAsset={{
                                                ...row,
                                                data_registrazione: new Date(row.data_registrazione),
                                                data_scadenza: row.data_scadenza ? new Date(row.data_scadenza) : null
                                            }}
                                        />
                                    )}>
                                    <Icon name={'edit'} />
                                </button>
                                <button
                                    className='text-lg active:scale-95 text-gray-500 hover:text-gray-600 duration-300 hover:scale-110'
                                    onClick={() => handleOpenModal(
                                        <ConfirmDelete
                                            onConfirm={() => handleDelete(row.id_asset)}
                                            onCancel={handleCloseModal}
                                            message={t('myAssets.deleteConfirm') || "Sei sicuro di voler eliminare l'Asset?"}
                                            elementNameToDelete={row.asset}
                                        />
                                    )}>
                                    <Icon name={'trash'} />
                                </button>
                            </div>
                        )
                    },
                ]}
                enableRowSelection={false}
                showSearchBar={false}
                maxHeight="50rem"
            />

            <ModalWrapper position='center' isOpen={isModalOpen} onClose={handleCloseModal}>
                {childrenModal}
            </ModalWrapper>
        </div>
    );
}
